import { useGetMeQuery } from "@/redux/feature/user/userApi";
import { useAddBrandDetailsMutation } from "@/redux/feature/videoDashboard/VideoDashboardApi";
import { useAppSelector } from "@/redux/hooks/hooks";
import React, { Dispatch, SetStateAction, useState, useEffect } from "react";
import toast from "react-hot-toast";

interface FormData {
  brand: string;
  brands_fb_url: string;
  brands_ig_username: string;
}

interface GetStartedModalProps {
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const initialForm: FormData = {
  brand: "",
  brands_fb_url: "",
  brands_ig_username: "",
};

const GetStartedModal: React.FC<GetStartedModalProps> = ({ setOpen }) => {
  const email = useAppSelector((state) => state.auth.user?.email);
  const { data } = useGetMeQuery(email);
  const companyName = data?.data?.businessName;

  const [formData, setFormData] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const [addBrandDetails, { isError }] = useAddBrandDetailsMutation();

  // Pre-populate the brand field when companyName is available
  useEffect(() => {
    if (companyName && !hasUserInteracted) {
      setFormData((prev) => ({
        ...prev,
        brand: companyName,
      }));
    }
  }, [companyName, hasUserInteracted]);

  // input handler
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "brand") {
      setHasUserInteracted(true);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // input validator
  const validate = () => {
    const newErrors: Partial<FormData> = {};
    if (!formData.brand.trim()) newErrors.brand = "Required";
    if (!formData.brands_fb_url.trim()) newErrors.brands_fb_url = "Required";
    if (!formData.brands_ig_username.trim())
      newErrors.brands_ig_username = "Required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // submit handler
  const handleSubmit = async () => {
    if (!validate()) return;

    setIsLoading(true);

    const payload = {
      email: email,
      brand: formData.brand,
      brands_fb_url: formData.brands_fb_url,
      brands_ig_username: formData.brands_ig_username,
    };

    try {
      await addBrandDetails(payload).unwrap();

      toast.success("Brand details are added you can get started now");
      setFormData(initialForm);

      localStorage.setItem("hasVisited", "true");
      setOpen(false);
      setFormData(initialForm);
    } catch (err) {
      console.error("Get started error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-black">Welcome! 🎉</h2>
        <p className="text-gray-600 text-sm mt-1">
          Let's get to know your brand better
        </p>
      </div>

      <div>
        <label className="block text-sm text-black mb-2">
          <span className="font-medium">Brand Name *</span>{" "}
          <span className="italic">( as per facebook profile page)</span>
        </label>
        <input
          type="text"
          name="brand"
          value={formData.brand}
          onChange={handleChange}
          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-1 ${
            errors.brand
              ? "border-red-500 ring-red-500"
              : "border-gray-300 focus:border-black focus:ring-black"
          }`}
          placeholder="e.g., Passionbits"
        />
        {errors.brand && (
          <p className="text-red-500 text-xs mt-1">{errors.brand}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-black mb-2">
          Facebook Page URL *
        </label>
        <input
          type="url"
          name="brands_fb_url"
          value={formData.brands_fb_url}
          onChange={handleChange}
          className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-1 ${
            errors.brands_fb_url
              ? "border-red-500 ring-red-500"
              : "border-gray-300 focus:border-black focus:ring-black"
          }`}
          placeholder="https://www.facebook.com/your-brand"
        />
        {errors.brands_fb_url && (
          <p className="text-red-500 text-xs mt-1">{errors.brands_fb_url}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-black mb-2">
          Instagram Username *
        </label>
        <div className="relative">
          <span className="absolute left-4 top-3.5 text-gray-500">@</span>
          <input
            type="text"
            name="brands_ig_username"
            value={formData.brands_ig_username}
            onChange={handleChange}
            className={`w-full pl-8 pr-4 py-3 border rounded-md focus:outline-none focus:ring-1 ${
              errors.brands_ig_username
                ? "border-red-500 ring-red-500"
                : "border-gray-300 focus:border-black focus:ring-black"
            }`}
            placeholder="your-username"
          />
        </div>
        {errors.brands_ig_username && (
          <p className="text-red-500 text-xs mt-1">
            {errors.brands_ig_username}
          </p>
        )}
      </div>

      <div className="border-t">
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="flex-1 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              Getting Started...
            </>
          ) : (
            "Get Started"
          )}
        </button>
      </div>
    </div>
  );
};

export default GetStartedModal;
